import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { get, getErrorMessage, post } from '../api/client';
import './LeadersSurveyPage.css';
import './SurveyEdits.css';

export function collectEdits(originalModules, draftModules, deletedQuestions = []) {
  const edits = [];
  const draftIds = new Set();
  (draftModules || []).forEach((mod) => {
    mod.subsections.forEach((sub) => {
      sub.questions.forEach((question) => draftIds.add(question.id));
    });
  });
  const originalIds = new Set();
  (originalModules || []).forEach((mod) => {
    const nextMod = (draftModules || []).find((item) => item.key === mod.key);
    if (!nextMod) return;
    if (nextMod.title.trim() !== mod.title) {
      edits.push({ kind: 'module', key: mod.key, title: nextMod.title.trim() });
    }
    mod.subsections.forEach((sub) => {
      const nextSub = nextMod.subsections.find((item) => item.code === sub.code);
      if (!nextSub) return;
      if (nextSub.title.trim() !== sub.title) {
        edits.push({ kind: 'subsection', code: sub.code, title: nextSub.title.trim() });
      }
      sub.questions.forEach((question) => {
        originalIds.add(question.id);
        const nextQuestion = nextSub.questions.find((item) => item.id === question.id);
        if (!nextQuestion) {
          edits.push({ kind: 'delete-question', id: question.id });
          return;
        }
        const questionEdit = { kind: 'question', id: question.id };
        let changed = false;
        if (nextQuestion.text.trim() !== question.text) {
          questionEdit.text = nextQuestion.text;
          changed = true;
        }
        if ((nextQuestion.help || '').trim() !== (question.help || '')) {
          questionEdit.help = nextQuestion.help || '';
          changed = true;
        }
        if (Boolean(nextQuestion.hidden) !== Boolean(question.hidden)) {
          questionEdit.hidden = Boolean(nextQuestion.hidden);
          changed = true;
        }
        if (changed) edits.push(questionEdit);
        (question.options || []).forEach((option) => {
          const nextOption = (nextQuestion.options || []).find((item) => item.key === option.key);
          if (nextOption && nextOption.text.trim() !== option.text) {
            edits.push({
              kind: 'option',
              questionId: question.id,
              key: option.key,
              text: nextOption.text,
            });
          }
        });
      });
    });
  });
  (draftModules || []).forEach((mod) => {
    mod.subsections.forEach((sub) => {
      sub.questions.forEach((question) => {
        if (originalIds.has(question.id)) return;
        edits.push({ kind: 'restore-question', id: question.id });
        const source = (deletedQuestions || []).find((item) => item.id === question.id);
        if (!source) return;
        const questionEdit = { kind: 'question', id: question.id };
        let changed = false;
        if (question.text.trim() !== source.text) {
          questionEdit.text = question.text;
          changed = true;
        }
        if ((question.help || '').trim() !== (source.help || '')) {
          questionEdit.help = question.help || '';
          changed = true;
        }
        if (Boolean(question.hidden) !== Boolean(source.hidden)) {
          questionEdit.hidden = Boolean(question.hidden);
          changed = true;
        }
        if (changed) edits.push(questionEdit);
      });
    });
  });
  return edits;
}

function updateQuestion(modules, questionId, patch) {
  return modules.map((mod) => ({
    ...mod,
    subsections: mod.subsections.map((sub) => ({
      ...sub,
      questions: sub.questions.map((question) => (
        question.id === questionId ? { ...question, ...patch } : question
      )),
    })),
  }));
}

export function QuestionPreview({ moduleTitle, sectionTitle, question }) {
  if (!question) return null;
  return (
    <section id="survey-edit-preview" className="survey-edits-preview" aria-label="Preview">
      <h2>Preview</h2>
      <p className="survey-edits-preview-kicker">{moduleTitle}</p>
      <h3>{sectionTitle}</h3>
      {question.hidden && <p className="survey-edits-hidden-note">Hidden from respondents. Existing answers stay saved.</p>}
      <article className={`my-survey-question ${question.hidden ? 'is-hidden' : ''}`}>
        {(question.type === 'choice' || question.type === 'multi') ? (
          <fieldset disabled className="my-survey-fieldset">
            <legend className="my-survey-qtext">{question.text || 'Question'}</legend>
            {question.help && <p className="my-survey-help">{question.help}</p>}
            {(question.options || []).map((option) => (
              <label key={option.key} className="my-survey-option">
                <input type={question.type === 'multi' ? 'checkbox' : 'radio'} name={`preview-${question.id}`} disabled />
                <span>{option.text}</span>
              </label>
            ))}
            {(question.options || []).some((option) => option.fillIn) && (
              <label className="my-survey-fill">
                <span>Other</span>
                <input type="text" disabled value="" placeholder="Fill-in" />
              </label>
            )}
          </fieldset>
        ) : (
          <label className="my-survey-text-label">
            <span className="my-survey-qtext">{question.text || 'Question'}</span>
            {question.help && <p className="my-survey-help">{question.help}</p>}
            <textarea rows={4} disabled value="" />
          </label>
        )}
      </article>
    </section>
  );
}

export function SurveyEditsView({ survey, log, onSave, saving, error }) {
  const [modules, setModules] = useState(survey.modules);
  const [note, setNote] = useState('');
  const [moduleKey, setModuleKey] = useState(survey.modules[0]?.key || '');
  const [sectionCode, setSectionCode] = useState(survey.modules[0]?.subsections[0]?.code || '');
  const [questionId, setQuestionId] = useState(survey.modules[0]?.subsections[0]?.questions[0]?.id || '');
  const closed = survey.status !== 'open';

  useEffect(() => {
    setModules(survey.modules);
    setNote('');
    setModuleKey(survey.modules[0]?.key || '');
    setSectionCode(survey.modules[0]?.subsections[0]?.code || '');
    setQuestionId(survey.modules[0]?.subsections[0]?.questions[0]?.id || '');
  }, [survey]);

  const currentModule = modules.find((mod) => mod.key === moduleKey) || modules[0];
  const currentSection = currentModule?.subsections.find((sub) => sub.code === sectionCode) || currentModule?.subsections[0];
  const question = currentSection?.questions.find((item) => item.id === questionId) || currentSection?.questions[0];

  const chooseModule = (key) => {
    const mod = modules.find((item) => item.key === key);
    setModuleKey(key);
    setSectionCode(mod?.subsections[0]?.code || '');
    setQuestionId(mod?.subsections[0]?.questions[0]?.id || '');
  };

  const chooseSection = (code) => {
    const sub = currentModule.subsections.find((item) => item.code === code);
    setSectionCode(code);
    setQuestionId(sub?.questions[0]?.id || '');
  };

  const save = () => {
    onSave(collectEdits(survey.modules, modules, survey.deletedQuestions), note);
  };

  const draftIds = new Set();
  modules.forEach((mod) => {
    mod.subsections.forEach((sub) => {
      sub.questions.forEach((item) => draftIds.add(item.id));
    });
  });
  const removedNow = [];
  survey.modules.forEach((mod) => {
    mod.subsections.forEach((sub) => {
      sub.questions.forEach((item, index) => {
        if (!draftIds.has(item.id)) {
          removedNow.push({
            ...item,
            moduleKey: mod.key,
            subsectionCode: sub.code,
            index,
          });
        }
      });
    });
  });
  const deleted = [
    ...removedNow,
    ...(survey.deletedQuestions || []).filter((item) => !draftIds.has(item.id) && !removedNow.some((row) => row.id === item.id)),
  ];

  const deleteQuestion = () => {
    if (!question) return;
    if (!window.confirm('Delete this question? People won\'t see it anymore.')) return;
    const id = question.id;
    setModules((current) => current.map((mod) => ({
      ...mod,
      subsections: mod.subsections.map((sub) => ({
        ...sub,
        questions: sub.questions.filter((item) => item.id !== id),
      })),
    })));
  };

  const restoreQuestion = (item) => {
    setModules((current) => current.map((mod) => {
      if (mod.key !== item.moduleKey) return mod;
      return {
        ...mod,
        subsections: mod.subsections.map((sub) => {
          if (sub.code !== item.subsectionCode) return sub;
          if (sub.questions.some((row) => row.id === item.id)) return sub;
          const questions = sub.questions.slice();
          const index = Math.min(item.index ?? questions.length, questions.length);
          questions.splice(index, 0, {
            id: item.id,
            type: item.type,
            text: item.text,
            help: item.help || '',
            hidden: Boolean(item.hidden),
            options: item.options || [],
          });
          return { ...sub, questions };
        }),
      };
    }));
    setModuleKey(item.moduleKey);
    setSectionCode(item.subsectionCode);
    setQuestionId(item.id);
  };

  return (
    <div className="survey-edits">
      <PageHeader title="Minor edits" section="leaders" />
      <p className="survey-edits-lead">
        Fix wording, titles, or hide a question. Question ids, types, and answer choices stay the same.
        Version {survey.surveyVersion}.
      </p>
      {closed && (
        <p className="survey-edits-banner" role="status">Reopen the survey before editing questions.</p>
      )}
      <fieldset disabled={closed || saving} className="survey-edits-form">
        <label>
          <span>Module</span>
          <select value={currentModule?.key || ''} onChange={(event) => chooseModule(event.target.value)}>
            {modules.map((mod) => <option key={mod.key} value={mod.key}>{mod.key}. {mod.title}</option>)}
          </select>
        </label>
        <label>
          <span>Module title</span>
          <input
            type="text"
            value={currentModule?.title || ''}
            onChange={(event) => setModules((current) => current.map((mod) => (
              mod.key === currentModule.key ? { ...mod, title: event.target.value } : mod
            )))}
          />
        </label>
        <label>
          <span>Section</span>
          <select value={currentSection?.code || ''} onChange={(event) => chooseSection(event.target.value)}>
            {(currentModule?.subsections || []).map((sub) => (
              <option key={sub.code} value={sub.code}>{sub.code}. {sub.title}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Section title</span>
          <input
            type="text"
            value={currentSection?.title || ''}
            onChange={(event) => setModules((current) => current.map((mod) => (
              mod.key !== currentModule.key ? mod : {
                ...mod,
                subsections: mod.subsections.map((sub) => (
                  sub.code === currentSection.code ? { ...sub, title: event.target.value } : sub
                )),
              }
            )))}
          />
        </label>
        <label>
          <span>Question</span>
          <select value={question?.id || ''} onChange={(event) => setQuestionId(event.target.value)}>
            {(currentSection?.questions || []).map((item) => (
              <option key={item.id} value={item.id}>{item.id}{item.hidden ? ' (hidden)' : ''}</option>
            ))}
          </select>
        </label>
        {question && (
          <>
            <p className="survey-edits-meta">Type: {question.type}. Id {question.id} cannot be changed.</p>
            <label>
              <span>Question text</span>
              <textarea rows={4} value={question.text} onChange={(event) => setModules((current) => updateQuestion(current, question.id, { text: event.target.value }))} />
            </label>
            <label>
              <span>Help text</span>
              <textarea rows={3} value={question.help || ''} onChange={(event) => setModules((current) => updateQuestion(current, question.id, { help: event.target.value }))} />
            </label>
            <label className="survey-edits-check">
              <input
                type="checkbox"
                checked={Boolean(question.hidden)}
                onChange={(event) => setModules((current) => updateQuestion(current, question.id, { hidden: event.target.checked }))}
              />
              <span>Hide this question</span>
            </label>
            <button type="button" className="survey-edits-delete" onClick={deleteQuestion}>
              Delete question
            </button>
            {(question.options || []).map((option) => (
              <label key={option.key}>
                <span>Choice {option.key}{option.fillIn ? ' (fill-in stays on)' : ''}</span>
                <input
                  type="text"
                  value={option.text}
                  onChange={(event) => setModules((current) => updateQuestion(current, question.id, {
                    options: question.options.map((item) => (
                      item.key === option.key ? { ...item, text: event.target.value } : item
                    )),
                  }))}
                />
              </label>
            ))}
          </>
        )}
      </fieldset>
      <QuestionPreview
        moduleTitle={currentModule ? `${currentModule.key}. ${currentModule.title}` : ''}
        sectionTitle={currentSection ? `${currentSection.code}. ${currentSection.title}` : ''}
        question={question}
      />
      <section className="survey-edits-deleted" aria-label="Deleted">
        <h2>Deleted</h2>
        {deleted.length === 0 && <p>No deleted questions.</p>}
        <ul>
          {deleted.map((item) => (
            <li key={item.id}>
              <p><strong>{item.id}</strong> {item.text}</p>
              <button type="button" onClick={() => restoreQuestion(item)}>Restore</button>
            </li>
          ))}
        </ul>
      </section>
      <label className="survey-edits-note">
        <span>Change note</span>
        <input
          type="text"
          value={note}
          maxLength={280}
          disabled={closed || saving}
          onChange={(event) => setNote(event.target.value)}
          placeholder="What did you fix?"
        />
      </label>
      {error && <p className="survey-edits-error" role="alert">{error}</p>}
      <button type="button" className="survey-edits-save" onClick={save} disabled={closed || saving}>
        {saving ? 'Saving…' : 'Save as new version'}
      </button>
      <section className="survey-edits-log" aria-label="Edit history">
        <h2>Recent edits</h2>
        {(log || []).length === 0 && <p>No edits yet.</p>}
        <ul>
          {(log || []).map((row) => (
            <li key={`${row.surveyVersion}-${row.questionId}-${row.field}-${row.at}`}>
              <p><strong>{row.questionId}</strong> · {row.field}</p>
              <p>{row.before} → {row.after}</p>
              <p className="survey-edits-log-meta">{row.changeNote} · {row.surveyVersion}</p>
              {row.field === 'deleted' && (
                <button type="button" onClick={() => {
                  const item = (survey.deletedQuestions || []).find((entry) => entry.id === row.questionId)
                    || deleted.find((entry) => entry.id === row.questionId);
                  if (item) restoreQuestion(item);
                }}
                >
                  Restore
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

const SurveyEditsPage = () => {
  const [survey, setSurvey] = useState(null);
  const [log, setLog] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useMemo(() => async () => {
    const [editor, edits] = await Promise.all([
      get('/api/leaders-survey/editor'),
      get('/api/leaders-survey/edits'),
    ]);
    if (!editor.ok) {
      setError(getErrorMessage(editor, 'Failed to load survey'));
      setLoading(false);
      return;
    }
    setSurvey(editor.data);
    setLog(edits.ok && Array.isArray(edits.data) ? edits.data : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load().catch(() => {
      setError('Failed to load survey');
      setLoading(false);
    });
  }, [load]);

  const onSave = async (edits, changeNote) => {
    if (!edits.length) {
      setError('No changes to save.');
      return;
    }
    setSaving(true);
    setError('');
    const response = await post('/api/leaders-survey/edits', { changeNote, edits });
    setSaving(false);
    if (!response.ok) {
      setError(getErrorMessage(response, 'Failed to save edits'));
      return;
    }
    await load();
  };

  if (loading) return <div className="survey-edits"><p>Loading…</p></div>;
  if (!survey) {
    return (
      <div className="survey-edits">
        <PageHeader title="Minor edits" section="leaders" />
        <p className="survey-edits-error" role="alert">{error || 'The survey is not available yet.'}</p>
      </div>
    );
  }

  return (
    <SurveyEditsView survey={survey} log={log} onSave={onSave} saving={saving} error={error} />
  );
};

export default SurveyEditsPage;
